import {ChangeDetectorRef, Injectable} from '@angular/core';
import {Item} from '../../models/Item';
import {Subject} from 'rxjs';
import {UiAdapterService} from '../uiAdapterService/ui-adapter.service';
import {DecimalPipe} from '@angular/common';


@Injectable({
  providedIn: 'root'
})

export class MeasurementService {

  static readonly KOEF_M_TO_FT = 3.28084;
  static readonly KOEF_KM_TO_MI = 0.621371;
  static readonly KOEF_SQ_M_TO_SQ_FT = 10.7639;

  private defaultSystem = window['MEASUREMENT'] || 'Metric';

  private thousandsSeparator = ',';

  private formatParameterConfg = {
    isShortNumberToFixed: true,
    fixed: 3,
  };

  public isFeetMeasurement = false;

  private measurementItemList = new Map<string, any>();
  private _onMeasurementItemListUpdated$ = new Subject<any>();

  get onMeasurementItemListUpdated$() {
    return this._onMeasurementItemListUpdated$.asObservable();
  }

  constructor(private ui: UiAdapterService, private decimalPipe: DecimalPipe) {
    this.ui.selectedItem$.subscribe((item) => {
      if (item instanceof Item) {
        if (this.isFeetMeasurement) {
          this.setSingleItemMeasurement(item);
        }
      }
    });
  }

  private skipConvertation(v: string): boolean {
    const skipOrValues = /\d+\s*.*\s*or\s*\d+\.?\d?.*/ig;
    return skipOrValues.test(v);
  }

  private convertCalculationConfig = [
    {
      isApplicable: (v) => {
        const regex = /(\d+\s*x\s*\d+\s*m)/;
        return regex.test(v);
      },
      convertVal: (input) => {
        const regex = /(\d+)(\s*)(x)(\s*)(\d+)(\s*)[m|f]/ig;
        return input.replaceAll(regex, (match, num1, space1, join, space2, num2, space3) => {
          return this.formatDecimal(this.metersToFeets(num1))
            + space1 + join + space2
            + this.formatDecimal(this.metersToFeets(num2)) + space3 + 'ft';
        });
      }
    },
    {
      isApplicable: (v) => {
        const regex = /(\d+\s*m)/;
        // note: skip values like “X km or Y m” or “X m or Y f”
        if (this.skipConvertation(v)) {
          return false;
        }
        return regex.test(v);
      },
      convertVal: (input) => {
        const regex = /(\d+)(\s*)m/ig;
        return input.replaceAll(regex, (match, num, space) => {
          return this.formatDecimal(this.metersToFeets(num)) + space + 'ft';
        });
      }
    }
  ];

  private convertConfigArr = [
    {
      symbol: '(m)',
      convertToSymbol: '(ft)',
      convertVal: this.metersToFeets,
      convertLabel: function (input) {
        return input.label.replace(this.symbol.toString(), this.convertToSymbol.toString());
      }
    },
    {
      symbol: '(m^2)',
      convertToSymbol: '(ft^2)',
      convertVal: this.squareMetersToSqFeets,
      convertLabel: function (input) {
        return input.label.replace(this.symbol.toString(), this.convertToSymbol.toString());
      }
    },
    {
      symbol: '(km)',
      convertToSymbol: '(mi)',
      convertVal: this.kilometersToMiles
    },
    {
      symbol: '(kph)',
      convertToSymbol: '(mph)',
      convertVal: this.kilometersToMiles
    },
    {
      symbol: '(km/h)',
      convertToSymbol: '(mi/h)',
      convertVal: this.kilometersToMiles
    },
    {
      exactMatch: true,
      symbol: 'Width',
      convertToSymbol: 'Width',
      convertVal: this.metersToFeets,
      convertLabel: function (input) {
        return input.label;
      }
    },
    {
      exactMatch: true,
      symbol: 'Length',
      convertToSymbol: 'Length',
      convertVal: this.metersToFeets,
      convertLabel: function (input) {
        return input.label;
      }
    },
    {
      exactMatch: true,
      symbol: 'Height',
      convertToSymbol: 'Height',
      convertVal: this.metersToFeets,
      convertLabel: function (input) {
        return input.label;
      }
    },
    {
      exactMatch: true,
      symbol: 'Depth',
      convertToSymbol: 'Depth',
      convertVal: this.metersToFeets,
      convertLabel: function (input) {
        return input.label;
      }
    }
  ];

  private unconvertConfigArr = [
    {
      symbol: '(ft)',
      convertToSymbol: '(m)',
      convertVal: this.feetsToMeter,
      convertLabel: function (input) {
        return input.label.replace(this.symbol.toString(), this.convertToSymbol.toString());
      }
    },
    {
      symbol: '(ft^2)',
      convertToSymbol: '(m^2)',
      convertVal: this.squareFeetsToSqMeters,
      convertLabel: function (input) {
        return input.label.replace(this.symbol.toString(), this.convertToSymbol.toString());
      }
    },
    {
      symbol: '(mi)',
      convertToSymbol: '(km)',
      convertVal: this.milesToKilometers
    },
    {
      symbol: '(mph)',
      convertToSymbol: '(kph)',
      convertVal: this.milesToKilometers
    },
    {
      symbol: '(mi/h)',
      convertToSymbol: '(km/h)',
      convertVal: this.milesToKilometers
    },
    {
      exactMatch: true,
      symbol: 'Width',
      convertToSymbol: 'Width',
      convertVal: this.feetsToMeter,
      convertLabel: function (input) {
        return input.label;
      }
    },
    {
      exactMatch: true,
      symbol: 'Length',
      convertToSymbol: 'Length',
      convertVal: this.feetsToMeter,
      convertLabel: function (input) {
        return input.label;
      }
    },
    {
      exactMatch: true,
      symbol: 'Height',
      convertToSymbol: 'Height',
      convertVal: this.feetsToMeter,
      convertLabel: function (input) {
        return input.label;
      }
    },
    {
      exactMatch: true,
      symbol: 'Depth',
      convertToSymbol: 'Depth',
      convertVal: this.feetsToMeter,
      convertLabel: function (input) {
        return input.label;
      }
    }
  ];

  public hasMeasurement(item, prop?): boolean {
    const inputs = item.config.getInputs();
    if (!inputs) {
      return false;
    }

    for (const name in inputs) {
      if (prop && name != prop) {
        continue;
      }
      for (let i = 0; i < this.convertConfigArr.length; i++) {
        const config = this.convertConfigArr[i];
        if (item.config.getInputProp(name, 'label')) {
          let hasSymbol = false;
          let hasConvertedSymbol = false;
          if (config.exactMatch) {
            hasSymbol = (item.config.getInputProp(name, 'label') === config.symbol);
            hasConvertedSymbol = (item.config.getInputProp(name, 'label') === config.convertToSymbol);
          } else {
            hasSymbol = (item.config.getInputProp(name, 'label').indexOf(config.symbol) >= 0);
            hasConvertedSymbol = (item.config.getInputProp(name, 'label').indexOf(config.convertToSymbol) >= 0);
          }
          if (hasSymbol || hasConvertedSymbol) {
            if (!this.measurementItemList.has(item.getId())) {
              this.measurementItemList.set(item.getId(), this.defaultSystem);
              // this.measurementItemList.set(item.getId(), (this.isFeetMeasurement) ? 'imperial' : 'metric');
              this._onMeasurementItemListUpdated$.next(true);
            }
            return true;
          }
        }
      }
    }

    return false;
  }

  setSingleItemMeasurement(item, flag?): void {
    if (flag == null || flag == 'undefined') {
      flag = this.isFeetMeasurement;
    }
    if (flag) {
      if (this.hasMeasurement(item) && this.getNextMeasurementSystemName(item).toLowerCase() === 'imperial') {
        this.changeMeasurement(item);
      }
    } else {
      if (this.hasMeasurement(item) && this.getNextMeasurementSystemName(item).toLowerCase() === 'metric') {
        this.changeMeasurement(item);
      }
    }
  }

  changeMeasurement(item: Item): void {
    let current = null;

    current = (this.measurementItemList.has(item.getId())) ? this.measurementItemList.get(item.getId()) : this.defaultSystem || 'metric';

    this.measurementItemList.set(item.getId(), (current.toLowerCase() === 'metric') ? 'imperial' : 'metric');
    this._onMeasurementItemListUpdated$.next(true);
  }

  getItemMeasurement(item){
    return this.measurementItemList.has(item.getId()) ? this.measurementItemList.get(item.getId()) : this.defaultSystem;
  }

  getMeasurementVal(item, input, inputName, index?, withoutLabel?): any {
    let _value = (index || index == 0) ? input.value[index] : input.value;
    if (_value == null || isNaN(_value)) {
      return null;
    }
    if (_value === '') {
      return null;
    }
    if (_value && _value.toString().trim() === '') {
      return null;
    }
    if (this.shouldConvertInput(item, input)) {
      for (let i = 0; i < this.convertConfigArr.length; i++) {
        const config = this.convertConfigArr[i];
        let hasSymbol = false;
        if (config.exactMatch) {
          hasSymbol = (input.label === config.symbol);
        } else {
          hasSymbol = (input.label.indexOf(config.symbol) >= 0);
        }
        if (hasSymbol) {
          item.config._convertedInputs[inputName] = Math.round((config.convertVal(_value) + Number.EPSILON) * 100) / 100;
          return this.formatCalculation(item, input, inputName, config, withoutLabel);
        }
      }
      return null;
    }
    item.config._convertedInputs[inputName] = Math.round((parseFloat(_value) + Number.EPSILON) * 100) / 100;
    return this.formatCalculation(item, input, inputName, null, withoutLabel);
  }


  getMeasurementCalc(item: Item, input, value, inputName) {
    let config = null;
    if (this.shouldConvertInput(item, input)) {
      for (let i = 0; i < this.convertConfigArr.length; i++) {
        config = this.convertConfigArr[i];

        let hasSymbol = false;
        if (config.exactMatch) {
          hasSymbol = (input.label === config.symbol);
        } else {
          hasSymbol = (input.label.indexOf(config.symbol) >= 0);
        }
        if (hasSymbol) {
          item.config._convertedInputs[inputName] = config.convertVal(value, this);
          return this.formatCalculation(item, input, inputName, config);
        }
      }
      return null;
    } else if (input.hasOwnProperty('calculation') && this.isFeetMeasurement) {
      for (let i = 0; i < this.convertCalculationConfig.length; i++) {
        const config = this.convertCalculationConfig[i];
        if (config.isApplicable(value)) {
          item.config._convertedInputs[inputName] = config.convertVal(value);
          return this.formatCalculation(item, input, inputName, config);
        }
      }
    }
    item.config._convertedInputs[inputName] = value;
    return this.formatCalculation(item, input, inputName, config);
  }

  private formatCalculation(item: Item, input, inputName: string, config?: any, withoutLabel?): string {
    let value = this.formatDecimal(item.config._convertedInputs[inputName]);
    if (this.isNumberType(value) && !withoutLabel) {
      value = value + ' ' + this.getMeasurementLabelSymbol(item, input);
    }
    return value;
  }

  getMeasurementLabel(item, input): string {
    for (let i = 0; i < this.convertConfigArr.length; i++) {
      const config = this.convertConfigArr[i];
      let hasSymbol = false;
      if (config.exactMatch) {
        hasSymbol = (input.label === config.symbol);
      } else {
        hasSymbol = (input.label.indexOf(config.symbol) >= 0);
      }
      if (hasSymbol) {
        if (config.exactMatch) {
          return input.label;
        } else {
          return input.label.substring(0, input.label.indexOf(config.symbol));
        }
      }
    }
    if (this.shouldConvertInput(item, input)) {
      return null;
    } else {
      return input.label;
    }
  }

  getMeasurementLabelSymbol(item, input): string {
    if (this.shouldConvertInput(item, input)) {
      for (let i = 0; i < this.convertConfigArr.length; i++) {
        const config = this.convertConfigArr[i];
        let hasSymbol = false;
        try {
          if (config.exactMatch) {
            hasSymbol = (input.label === config.symbol);
          } else {
            hasSymbol = (input.label.indexOf(config.symbol) >= 0);
          }
        } catch (e) {
          hasSymbol = false;
        }

        if (hasSymbol) {
          if (config.exactMatch) {
            return 'ft';
          } else {
            return config.convertToSymbol.replace(/[\(\)]/g, '');
          }
        }
      }
      return null;
    }

    for (let i = 0; i < this.convertConfigArr.length; i++) {
      const config = this.convertConfigArr[i];
      let hasSymbol = false;
      try {
        if (config.exactMatch) {
          hasSymbol = (input.label === config.symbol);
        } else {
          hasSymbol = (input.label.indexOf(config.symbol) >= 0);
        }
      } catch (e) {
        hasSymbol = false;
      }
      if (hasSymbol) {
        if (config.exactMatch) {
          return 'm';
        } else {
          return config.symbol.replace(/[\(\)]/g, '');
        }
      }
    }
    return '';
  }

  public getNextMeasurementSystemName(item: Item): string {

    if (this.measurementItemList.has(item.getId())) {
      return (this.measurementItemList.get(item.getId()).toLowerCase() === 'metric') ? 'Imperial' : 'Metric';
    }

    if (this.defaultSystem) {
      return (this.defaultSystem.toLowerCase() === 'metric') ? 'Imperial' : 'Metric';
    }

    return 'Imperial';
  }

  private shouldConvertInput(item, input) {

    if (!input.hasOwnProperty('label')) {
      return;
    }

    let isConvertedInput = false;
    for (let i = 0; i < this.convertConfigArr.length; i++) {
      const config = this.convertConfigArr[i];
      let hasSymbol = false;
      if (config.exactMatch) {
        hasSymbol = (input.label === config.symbol);
      } else {
        hasSymbol = (input.label.indexOf(config.symbol) >= 0);
      }
      if (hasSymbol) {
        isConvertedInput = true;
      }
    }

    if (!isConvertedInput) {
      return false;
    }

    if (this.measurementItemList.has(item.getId())) {
      if (!this.isFeetMeasurement) {
        return false;
      }
      return this.measurementItemList.get(item.getId()).toLowerCase() == 'imperial';
    }

    // if (item.config.getInputByName(Item.MEASUREMENT_INPUT_NAME) && item.config.getInputByName(Item.MEASUREMENT_INPUT_NAME).value.toLowerCase() == 'imperial'){
    //   return true
    // }
    return this.defaultSystem.toLowerCase() == 'imperial';
  }

  public getUnconvertedInputVal(item, input, value): string {
    if (this.measurementItemList.has(item.getId()) && this.measurementItemList.get(item.getId()).toLowerCase() !== 'metric') {
      // if (item.config.getInputByName(Item.MEASUREMENT_INPUT_NAME) && item.config.getInputByName(Item.MEASUREMENT_INPUT_NAME).value.toLowerCase() != 'metric'){
      for (let i = 0; i < this.unconvertConfigArr.length; i++) {
        const config = this.unconvertConfigArr[i];
        let hasConvertedSymbol = false;
        if (config.exactMatch) {
          hasConvertedSymbol = (input.label === config.convertToSymbol);
        } else {
          hasConvertedSymbol = (input.label.indexOf(config.convertToSymbol) >= 0);
        }
        if (hasConvertedSymbol) {
          return this.formatDecimal(config.convertVal(value));
        }
      }
    }

    return value;
  }

  private kilometersToMiles(val, classRef?): number {
    return parseFloat(val) * MeasurementService.KOEF_KM_TO_MI;
  }

  private milesToKilometers(val, classRef?): number {
    return parseFloat(val) / MeasurementService.KOEF_KM_TO_MI;
  }

  private metersToFeets(val, classRef?): number {
    return parseFloat(val) * MeasurementService.KOEF_M_TO_FT;
  }

  private feetsToMeter(val): number {
    return parseFloat(val) / MeasurementService.KOEF_M_TO_FT;
  }

  private squareMetersToSqFeets(val, classRef?): number {
    return parseFloat(val) * MeasurementService.KOEF_SQ_M_TO_SQ_FT;
  }

  private squareFeetsToSqMeters(val): number {
    return parseFloat(val) / MeasurementService.KOEF_SQ_M_TO_SQ_FT;
  }

  private isNumberType(val: any): boolean {
    return typeof val === 'number' || (typeof val === 'string' && !isNaN(Number(val)));
  }

  public formatDecimal(val): any {
    if (this.formatParameterConfg.isShortNumberToFixed) {
      if (this.isNumberType(val)) {
        const transformedValue = this.decimalPipe.transform(val, `1.0-${this.formatParameterConfg.fixed}`);
        if (transformedValue === null) {
          console.error("Couldn't transform decimal value:", val)
          return val;
        }
        return transformedValue.replace(this.thousandsSeparator, '');
      }
    }
    return val;
  }
}
